import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  LinearProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import RestoreIcon from "@mui/icons-material/Restore";
import HistoryIcon from "@mui/icons-material/History";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InventoryIcon from "@mui/icons-material/Inventory";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TimelineIcon from "@mui/icons-material/Timeline";
import SettingsIcon from "@mui/icons-material/Settings";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Tooltip } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { formatDate } from "../../utils/formatDate";

// `input.warehouse` / `input.channel` are multi-select now — a run can carry
// several of either. Render whatever's there (old scalar data included) as a
// comma-joined label.
const warehouseName = (w) =>
  !w ? "" : typeof w === "string" ? w : w.name || w.warehouse_code || "";
const joinedLabel = (value, toLabel = (v) => v) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .map(toLabel)
    .filter(Boolean)
    .join(", ") || "N/A";

const calculateReorderUrgency = (reorderByDate) => {
  if (!reorderByDate) return { daysUntil: null, urgency: "none", label: "" };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const reorderDate = new Date(reorderByDate);
  reorderDate.setHours(0, 0, 0, 0);

  const timeDiff = reorderDate - today;
  const daysUntil = Math.ceil(timeDiff / (1000 * 3600 * 24));

  let urgency = "normal";
  let label = "";

  if (daysUntil < 0) {
    urgency = "overdue";
    label = `OVERDUE (${Math.abs(daysUntil)} days ago)`;
  } else if (daysUntil === 0) {
    urgency = "critical";
    label = "TODAY";
  } else if (daysUntil <= 3) {
    urgency = "critical";
    label = `${daysUntil} day${daysUntil > 1 ? "s" : ""} remaining`;
  } else if (daysUntil <= 7) {
    urgency = "warning";
    label = `${daysUntil} days remaining`;
  } else {
    urgency = "normal";
    label = `${daysUntil} days remaining`;
  }

  return { daysUntil, urgency, label };
};

const UrgentReorderCard = styled(Paper)(({
  theme,
  urgencyLevel = "normal",
}) => {
  const getUrgencyStyle = () => {
    if (urgencyLevel === "critical") {
      return {
        borderLeft: `4px solid ${theme.palette.error.main}`,
        background: `linear-gradient(135deg, ${theme.palette.error.main}12, ${theme.palette.error.main}06)`,
        boxShadow: `0 0 20px ${theme.palette.error.main}20`,
      };
    } else if (urgencyLevel === "warning") {
      return {
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        background: `linear-gradient(135deg, ${theme.palette.warning.main}12, ${theme.palette.warning.main}06)`,
        boxShadow: `0 0 16px ${theme.palette.warning.main}18`,
      };
    }
    return {
      borderLeft: `3px solid ${theme.palette.success.main}`,
      background: `linear-gradient(135deg, ${theme.palette.success.main}08, ${theme.palette.success.main}04)`,
    };
  };

  return {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
    ...getUrgencyStyle(),
  };
});

const StyledTimelineItem = styled(TimelineItem)(({ theme }) => ({
  "&:before": {
    display: "none",
  },
}));

const MetricCard = styled(Paper)(({ theme, color = "primary" }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  background: `linear-gradient(135deg, ${theme.palette["secondary"].main}08, ${theme.palette["secondary"].main}04)`,
  borderLeft: `3px solid ${theme.palette["secondary"].main}`,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: theme.shadows[2],
  },
}));

const InputParamCard = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: "var(--bg-highlight)",
        // minWidth: "120px",
        flex: "1 1 auto",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontFamily: "Poppins",
          fontWeight: 600,
          color: theme.palette.text.secondary,
          fontSize: "10px",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          display: "block",
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: "Poppins",
          fontWeight: 600,
          color: theme.palette.primary.main,
          fontSize: "14px",
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
};

const ForecastHistoryModal = ({
  open,
  onClose,
  forecastHistory = [],
  onRestore,
  loading = false,
  sku,
  displayExplanation,
}) => {
  const theme = useTheme();
  const [showExplanation, setShowExplanation] = React.useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      full: date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  const formatMetric = (value, label) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "number") {
      if (label?.toLowerCase().includes("date")) {
        return formatDate(value);
      }
      if (label?.toLowerCase().includes("demand")) {
        return isNaN(value) ? value.toLocaleString() : Number(value).toFixed(2);
      }
      return isNaN(value) ? value.toLocaleString() : Math.round(Number(value));
    }
    return value;
  };

  const getVersionColor = (index, total) => {
    const version = total - index;
    if (version === total) return "success"; // Latest version
    if (version >= total - 2) return "primary"; // Recent versions
    return "grey"; // Older versions
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Enhanced Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.primary.main}04)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 48,
                height: 48,
              }}
            >
              <HistoryIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                Forecast History
              </Typography>
              {sku && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    SKU: {sku}
                  </Typography>
                  <Chip
                    label={`${forecastHistory.length} versions`}
                    size="small"
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "11px",
                      fontWeight: 500,
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: "var(--bg-primary)",
              "&:hover": {
                bgcolor: "var(--hover-bg)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : theme.palette.grey[50],
        }}
      >
        {loading ? (
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <AssessmentIcon color="primary" />
              <Typography
                variant="h6"
                sx={{ fontFamily: "Poppins", fontWeight: 600 }}
              >
                Loading Forecast History
              </Typography>
            </Box>
            <LinearProgress sx={{ borderRadius: 2, height: 8 }} />
          </Box>
        ) : forecastHistory.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
              p: 4,
            }}
          >
            <HistoryIcon
              sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: theme.palette.text.secondary,
                mb: 1,
              }}
            >
              No Forecast History Available
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Poppins",
                color: theme.palette.text.secondary,
                textAlign: "center",
              }}
            >
              This SKU doesn't have any forecast history yet. Run a forecast to
              create the first version.
            </Typography>
          </Box>
        ) : (
          <Timeline
            sx={{
              p: 3,
              "& .MuiTimelineItem-root:before": {
                display: "none",
              },
            }}
          >
            {forecastHistory.map((item, index) => {
              const dateInfo = formatDate(item.createdAt);
              const versionColor = getVersionColor(
                index,
                forecastHistory.length,
              );
              const isLatest = index === 0;

              return (
                <StyledTimelineItem key={item._id || index}>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        bgcolor: isLatest
                          ? theme.palette.success.main
                          : theme.palette.primary.main,
                        width: 40,
                        height: 40,
                        boxShadow: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "white",
                          fontWeight: 700,
                          fontSize: "12px",
                          fontFamily: "Poppins",
                        }}
                      >
                        {forecastHistory.length - index}
                      </Typography>
                    </TimelineDot>
                    {index < forecastHistory.length - 1 && (
                      <TimelineConnector sx={{ width: 2 }} />
                    )}
                  </TimelineSeparator>

                  <TimelineContent sx={{ py: "16px", px: 2 }}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: isLatest ? 4 : 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: 6,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 3,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontFamily: "Poppins",
                                  fontWeight: 700,
                                  color: theme.palette.text.primary,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                Version {forecastHistory.length - index}
                                <Chip
                                  label={
                                    item.accepted ? "Accepted" : "Discarded"
                                  }
                                  size="small"
                                  color={item.accepted ? "success" : "error"}
                                  variant={
                                    item.accepted ? "filled" : "outlined"
                                  }
                                  sx={{
                                    fontFamily: "Poppins",
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    height: 20,
                                  }}
                                />
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "Poppins",
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                Forecast generated on {dateInfo.full}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              variant="contained"
                              startIcon={<RestoreIcon />}
                              onClick={() => onRestore && onRestore(item)}
                              sx={{
                                textTransform: "none",
                                fontFamily: "Poppins",
                                fontSize: "12px",
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                boxShadow: 2,
                                "&:hover": {
                                  boxShadow: 4,
                                },
                                "&:disabled": {
                                  opacity: 0.6,
                                },
                              }}
                            >
                              {"Restore"}
                            </Button>
                          </Box>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        {/* Metrics Section */}
                        {item.metrics && (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontFamily: "Poppins",
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                mb: 1.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <AssessmentIcon sx={{ fontSize: 18 }} />
                              Key Metrics
                            </Typography>
                            <Box
                              sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                            >
                              <MetricCard color="primary">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <InventoryIcon
                                    sx={{
                                      fontSize: 16,
                                      color: theme.palette.text.primary,
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        fontSize: "10px",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                        lineHeight: 1,
                                      }}
                                    >
                                      On Hand
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                        fontSize: "14px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {formatMetric(item.metrics.total_on_hand)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MetricCard>
                              <MetricCard color="info">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <TrendingUpIcon
                                    sx={{
                                      fontSize: 16,
                                      color: theme.palette.text.primary,
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        fontSize: "10px",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                        lineHeight: 1,
                                      }}
                                    >
                                      In Transit
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                        fontSize: "14px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {formatMetric(
                                        item.metrics.total_in_transit,
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MetricCard>
                              <MetricCard color="warning">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        fontSize: "10px",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                        lineHeight: 1,
                                      }}
                                    >
                                      Reorder Point
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                        fontSize: "14px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {formatMetric(item.metrics.reorder_point)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MetricCard>
                              <MetricCard color="success">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 600,
                                        color: theme.palette.text.secondary,
                                        fontSize: "10px",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                        lineHeight: 1,
                                      }}
                                    >
                                      Safety Stock
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                        fontSize: "14px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {item.metrics.safety_stock_used
                                        ? formatMetric(
                                            item.metrics.safety_stock_used,
                                          )
                                        : "N/A"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MetricCard>
                              <MetricCard color="secondary">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <TrendingUpIcon
                                    sx={{
                                      fontSize: 16,
                                      color: theme.palette.text.primary,
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        fontSize: "10px",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                        lineHeight: 1,
                                      }}
                                    >
                                      Avg Daily Demand
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                        fontSize: "14px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {formatMetric(
                                        item.metrics.avg_daily_demand,
                                        "demand",
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MetricCard>
                              <MetricCard color="error">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <ScheduleIcon
                                    sx={{
                                      fontSize: 16,
                                      color: theme.palette.text.primary,
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        fontSize: "10px",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                        lineHeight: 1,
                                      }}
                                    >
                                      Lead Time
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                        fontSize: "14px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {item.metrics.lead_time_days
                                        ? formatMetric(
                                            item.metrics.lead_time_days,
                                          ) + " days"
                                        : "N/A"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MetricCard>
                              <MetricCard color="success">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <ShoppingCartIcon
                                    sx={{
                                      fontSize: 16,
                                      color: theme.palette.text.primary,
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        fontSize: "10px",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                        lineHeight: 1,
                                      }}
                                    >
                                      Recomended Reorder Quantity
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                        fontSize: "14px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {formatMetric(
                                        item.metrics.recommended_order_qty,
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MetricCard>
                              {item.metrics.reorder_by_date ? (
                                (() => {
                                  const urgencyInfo = calculateReorderUrgency(
                                    item.metrics.reorder_by_date,
                                  );
                                  const isUpcoming =
                                    urgencyInfo.urgency !== "normal" &&
                                    urgencyInfo.urgency !== "none";

                                  return (
                                    <UrgentReorderCard
                                      urgencyLevel={
                                        isUpcoming
                                          ? urgencyInfo.urgency
                                          : "normal"
                                      }
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 48,
                                            height: 48,
                                            borderRadius: "50%",
                                            background:
                                              urgencyInfo.urgency === "critical"
                                                ? `${theme.palette.error.main}20`
                                                : urgencyInfo.urgency ===
                                                    "warning"
                                                  ? `${theme.palette.warning.main}20`
                                                  : `${theme.palette.success.main}20`,
                                          }}
                                        >
                                          {urgencyInfo.urgency ===
                                          "critical" ? (
                                            <ErrorIcon
                                              sx={{
                                                color: theme.palette.error.main,
                                                fontSize: 24,
                                                fontWeight: 700,
                                              }}
                                            />
                                          ) : urgencyInfo.urgency ===
                                            "warning" ? (
                                            <WarningAmberIcon
                                              sx={{
                                                color:
                                                  theme.palette.warning.main,
                                                fontSize: 24,
                                              }}
                                            />
                                          ) : (
                                            <CalendarTodayIcon
                                              sx={{
                                                color:
                                                  theme.palette.success.main,
                                                fontSize: 24,
                                              }}
                                            />
                                          )}
                                        </Box>

                                        <Box sx={{ flex: 1 }}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontFamily: "Poppins",
                                              fontWeight: 700,
                                              color:
                                                urgencyInfo.urgency ===
                                                "critical"
                                                  ? theme.palette.error.main
                                                  : urgencyInfo.urgency ===
                                                      "warning"
                                                    ? theme.palette.warning.main
                                                    : theme.palette.success
                                                        .main,
                                              fontSize: "10px",
                                              textTransform: "uppercase",
                                              letterSpacing: 0.8,
                                              lineHeight: 1,
                                              display: "block",
                                              mb: 0.5,
                                            }}
                                          >
                                            {urgencyInfo.urgency === "critical"
                                              ? "⚠ Action Required"
                                              : urgencyInfo.urgency ===
                                                  "warning"
                                                ? "⏰ Upcoming"
                                                : "Next Reorder"}
                                          </Typography>

                                          <Typography
                                            variant="h6"
                                            sx={{
                                              fontFamily: "Poppins",
                                              fontWeight: 800,
                                              color:
                                                urgencyInfo.urgency ===
                                                "critical"
                                                  ? theme.palette.error.main
                                                  : urgencyInfo.urgency ===
                                                      "warning"
                                                    ? theme.palette.warning.main
                                                    : theme.palette.text
                                                        .primary,
                                              fontSize: "16px",
                                              lineHeight: 1.2,
                                              mb: 0.5,
                                            }}
                                          >
                                            {new Date(
                                              item.metrics.reorder_by_date,
                                            )?.toLocaleDateString("en-US", {
                                              month: "short",
                                              day: "2-digit",
                                              year: "numeric",
                                            })}
                                          </Typography>

                                          {isUpcoming && (
                                            <Chip
                                              label={urgencyInfo.label}
                                              size="small"
                                              icon={
                                                urgencyInfo.urgency ===
                                                "critical" ? (
                                                  <ErrorIcon
                                                    sx={{ fontSize: 14 }}
                                                  />
                                                ) : (
                                                  <WarningAmberIcon
                                                    sx={{ fontSize: 14 }}
                                                  />
                                                )
                                              }
                                              sx={{
                                                height: 20,
                                                fontSize: "11px",
                                                fontFamily: "Poppins",
                                                fontWeight: 700,
                                                bgcolor:
                                                  urgencyInfo.urgency ===
                                                  "critical"
                                                    ? `${theme.palette.error.main}15`
                                                    : `${theme.palette.warning.main}15`,
                                                color:
                                                  urgencyInfo.urgency ===
                                                  "critical"
                                                    ? theme.palette.error.main
                                                    : theme.palette.warning
                                                        .main,
                                                border: `1px solid ${
                                                  urgencyInfo.urgency ===
                                                  "critical"
                                                    ? theme.palette.error.main
                                                    : theme.palette.warning.main
                                                }`,
                                              }}
                                            />
                                          )}
                                        </Box>
                                      </Box>
                                    </UrgentReorderCard>
                                  );
                                })()
                              ) : (
                                <MetricCard>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    <CalendarTodayIcon
                                      sx={{
                                        fontSize: 20,
                                        color: theme.palette.text.secondary,
                                      }}
                                    />
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontFamily: "Poppins",
                                          fontWeight: 600,
                                          color: theme.palette.text.secondary,
                                          fontSize: "11px",
                                          textTransform: "uppercase",
                                          letterSpacing: 0.5,
                                          lineHeight: 1,
                                        }}
                                      >
                                        Next Reorder Date
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                          color: theme.palette.text.secondary,
                                          fontSize: "18px",
                                          lineHeight: 1,
                                        }}
                                      >
                                        N/A
                                      </Typography>
                                    </Box>
                                  </Box>
                                </MetricCard>
                              )}
                            </Box>
                          </Box>
                        )}
                        {/* Input Parameters */}
                        {item.input && (
                          <Accordion
                            defaultExpanded={false}
                            sx={{
                              boxShadow: "none",
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              "&:before": {
                                display: "none",
                              },
                              "&.Mui-expanded": {
                                margin: 0,
                              },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                minHeight: 48,
                                "&.Mui-expanded": {
                                  minHeight: 48,
                                },
                                "& .MuiAccordionSummary-content": {
                                  margin: "12px 0",
                                },
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontFamily: "Poppins",
                                  fontWeight: 700,
                                  color: theme.palette.text.primary,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <SettingsIcon sx={{ fontSize: 18 }} />
                                Forecast Parameters
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 2, pt: 0 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 2,
                                }}
                              >
                                <InputParamCard
                                  label="Forecast By"
                                  value={item.input.forecast_by || "sku"}
                                />
                                <InputParamCard
                                  label="Type"
                                  value={item.input.type || "Monthly"}
                                />
                                <InputParamCard
                                  label="Horizon"
                                  value={`${item.input.horizon} periods`}
                                />
                                <InputParamCard
                                  label="Warehouse"
                                  value={joinedLabel(
                                    item.input?.warehouse,
                                    warehouseName,
                                  )}
                                />
                                <InputParamCard
                                  label="Provider"
                                  value={joinedLabel(item.input?.channel)}
                                />
                                <InputParamCard
                                  label="Discount Percentage"
                                  value={
                                    item.input.discount
                                      ? `${item.input.discount} %`
                                      : "N/A"
                                  }
                                />
                                {item.input.forecast_by?.toLowerCase() ===
                                "product" ? (
                                  <InputParamCard
                                    label="Product"
                                    value={item.input.product?.name || "N/A"}
                                  />
                                ) : item.input.forecast_by?.toLowerCase() ===
                                  "category" ? (
                                  <InputParamCard
                                    label="Category"
                                    value={
                                      item.input.category ||
                                      item.input.product_type ||
                                      "N/A"
                                    }
                                  />
                                ) : (
                                  <InputParamCard
                                    label="SKU"
                                    value={item.input.sku || "N/A"}
                                  />
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        )}
                        {/* Forecast Data Points */}
                        {item.forecast_adjusted &&
                          item.forecast_adjusted.length > 0 && (
                            <Accordion
                              defaultExpanded={false}
                              sx={{
                                boxShadow: "none",
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                "&:before": {
                                  display: "none",
                                },
                                "&.Mui-expanded": {
                                  margin: 0,
                                },
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                  minHeight: 48,
                                  "&.Mui-expanded": {
                                    minHeight: 48,
                                  },
                                  "& .MuiAccordionSummary-content": {
                                    margin: "12px 0",
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    pr: 1,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontFamily: "Poppins",
                                      fontWeight: 700,
                                      color: theme.palette.text.primary,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      flex: 1,
                                    }}
                                  >
                                    <TrendingUpIcon sx={{ fontSize: 18 }} />
                                    Forecasted Inventory
                                    <Chip
                                      label={`${item.forecast_adjusted.length} periods`}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontFamily: "Poppins",
                                        fontSize: "10px",
                                        ml: 1,
                                      }}
                                    />
                                  </Typography>
                                  {item.explanation && displayExplanation && (
                                    <Tooltip
                                      title={
                                        showExplanation
                                          ? "Hide Forecast Rationale"
                                          : "Why this forecast?"
                                      }
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowExplanation((prev) => !prev);
                                        }}
                                        sx={{
                                          width: 26,
                                          height: 26,
                                          ml: 1,
                                          bgcolor: showExplanation
                                            ? theme.palette.primary.main
                                            : `${theme.palette.primary.main}14`,
                                          color: showExplanation
                                            ? "#fff"
                                            : theme.palette.primary.main,
                                          border: `1.5px solid ${
                                            showExplanation
                                              ? theme.palette.primary.main
                                              : `${theme.palette.primary.main}40`
                                          }`,
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            bgcolor: showExplanation
                                              ? theme.palette.primary.dark
                                              : `${theme.palette.primary.main}28`,
                                            transform: "scale(1.1)",
                                          },
                                        }}
                                      >
                                        <HelpOutlineIcon
                                          sx={{ fontSize: 13 }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 0.5, pt: 0 }}>
                                {/* AI Explanation Panel */}

                                <Box
                                  sx={{
                                    maxHeight: "250px",
                                    overflowY: "auto",
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    p: 1,
                                    bgcolor: theme.palette.background.paper,
                                  }}
                                >
                                  <Grid container spacing={0.5}>
                                    {item.forecast_adjusted.map(
                                      (point, idx) => (
                                        <Grid
                                          item
                                          xs={12}
                                          sm={6}
                                          md={Math.max(
                                            12 / item.forecast_adjusted.length,
                                            1,
                                          )}
                                          key={idx}
                                        >
                                          <Paper
                                            sx={{
                                              p: 0.75,
                                              borderRadius: 0.75,
                                              border: `1px solid ${theme.palette.divider}`,
                                              bgcolor:
                                                theme.palette.mode === "dark"
                                                  ? theme.palette.background
                                                      .default
                                                  : theme.palette.grey[50],
                                              transition: "all 0.2s ease",
                                              "&:hover": {
                                                bgcolor:
                                                  theme.palette.action.hover,
                                                transform: "translateY(-1px)",
                                              },
                                            }}
                                          >
                                            <Box sx={{ textAlign: "center" }}>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontFamily: "Poppins",
                                                  fontWeight: 600,
                                                  fontSize: "12px",
                                                  color:
                                                    theme.palette.text.primary,
                                                  mb: 0.125,
                                                  lineHeight: 1.1,
                                                }}
                                              >
                                                {new Date(
                                                  point.date,
                                                )?.toLocaleDateString("en-US", {
                                                  timeZone: "UTC",
                                                  month: "short",
                                                  day: "2-digit",
                                                  year: "numeric",
                                                })}
                                              </Typography>

                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontFamily: "Poppins",
                                                  fontWeight: 700,
                                                  fontSize: "14px",
                                                  color:
                                                    theme.palette.primary.main,
                                                  lineHeight: 1.1,
                                                }}
                                              >
                                                {formatMetric(
                                                  point.predicted_qty,
                                                ) + " units"}
                                              </Typography>
                                            </Box>
                                          </Paper>
                                        </Grid>
                                      ),
                                    )}
                                  </Grid>
                                  {item.forecast_adjusted.length > 10 && (
                                    <Box sx={{ textAlign: "center", mt: 2 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontFamily: "Poppins",
                                          fontSize: "12px",
                                          color: theme.palette.text.secondary,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        ... and{" "}
                                        {item.forecast_adjusted.length - 10}{" "}
                                        more periods
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                                {showExplanation && item.explanation && (
                                  <Box
                                    sx={{
                                      mb: 1.5,
                                      borderRadius: 2,
                                      overflow: "hidden",
                                      border: `1.5px solid ${theme.palette.primary.main}30`,
                                      background: `linear-gradient(135deg, ${theme.palette.primary.main}06, ${theme.palette.primary.main}02)`,
                                    }}
                                  >
                                    {/* Header bar */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 1.5,
                                        py: 1,
                                        borderBottom: `1px solid ${theme.palette.primary.main}20`,
                                        bgcolor: `${theme.palette.primary.main}10`,
                                      }}
                                    >
                                      <AutoAwesomeIcon
                                        sx={{
                                          fontSize: 14,
                                          color: theme.palette.primary.main,
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                          fontSize: "12px",
                                          color: theme.palette.primary.main,
                                          letterSpacing: 0.3,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        Forecast Rationale
                                      </Typography>
                                      {/* <Chip
                                        label="AI Generated"
                                        size="small"
                                        sx={{
                                          ml: "auto",
                                          fontFamily: "Poppins",
                                          fontSize: "9px",
                                          fontWeight: 600,
                                          height: 18,
                                          bgcolor: `${theme.palette.primary.main}18`,
                                          color: theme.palette.primary.main,
                                          border: `1px solid ${theme.palette.primary.main}30`,
                                        }}
                                      /> */}
                                    </Box>
                                    {/* HTML content */}
                                    <Box
                                      sx={{
                                        px: 2,
                                        py: 1.5,
                                        maxHeight: 260,
                                        overflowY: "auto",
                                        "& h1, & h2, & h3, & h4": {
                                          fontFamily: "Poppins",
                                          fontWeight: 700,
                                          color: theme.palette.text.primary,
                                          mt: 1,
                                          mb: 0.5,
                                        },
                                        "& p": {
                                          fontFamily: "Poppins",
                                          fontSize: "12px",
                                          color: theme.palette.text.secondary,
                                          lineHeight: 1.7,
                                          mb: 1,
                                          mt: 0,
                                        },
                                        "& ul, & ol": { pl: 2.5, mb: 1 },
                                        "& li": {
                                          fontFamily: "Poppins",
                                          fontSize: "12px",
                                          color: theme.palette.text.secondary,
                                          lineHeight: 1.7,
                                          mb: 0.25,
                                        },
                                        "& strong, & b": {
                                          color: theme.palette.text.primary,
                                          fontWeight: 600,
                                        },
                                        "& table": {
                                          width: "100%",
                                          borderCollapse: "collapse",
                                          fontSize: "11px",
                                          fontFamily: "Poppins",
                                          mb: 1,
                                        },
                                        "& th": {
                                          bgcolor: `${theme.palette.primary.main}14`,
                                          color: theme.palette.primary.main,
                                          fontWeight: 700,
                                          p: "5px 8px",
                                          textAlign: "left",
                                          borderBottom: `2px solid ${theme.palette.primary.main}30`,
                                        },
                                        "& td": {
                                          p: "4px 8px",
                                          color: theme.palette.text.secondary,
                                          borderBottom: `1px solid ${theme.palette.divider}`,
                                        },
                                        "& code": {
                                          fontFamily: "monospace",
                                          bgcolor:
                                            theme.palette.mode === "dark"
                                              ? theme.palette.grey[800]
                                              : theme.palette.grey[100],
                                          borderRadius: "3px",
                                          px: 0.5,
                                          fontSize: "11px",
                                        },
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: item.explanation,
                                      }}
                                    />
                                  </Box>
                                )}
                              </AccordionDetails>
                            </Accordion>
                          )}
                        {item.forecast_adjusted &&
                        item.forecast_adjusted.length ? (
                          <Accordion
                            defaultExpanded={false}
                            sx={{
                              boxShadow: "none",
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              "&:before": { display: "none" },
                              "&.Mui-expanded": { margin: 0 },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={
                                {
                                  // minHeight: 56,
                                  // "&.Mui-expanded": { minHeight: 56 },
                                  // "& .MuiAccordionSummary-content": {
                                  //   margin: "16px 0",
                                  // },
                                }
                              }
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontFamily: "Poppins",
                                  fontWeight: 700,
                                  color: theme.palette.text.primary,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <TimelineIcon sx={{ fontSize: 18 }} />
                                Inventory by Region
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 3, pt: 0 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Box
                                    sx={{ overflowX: "auto", display: "flex" }}
                                  >
                                    {[
                                      {
                                        warehouse: "West Coast",
                                        percent: 28,
                                      },
                                      {
                                        warehouse: "South Central",
                                        percent: 18,
                                      },
                                      {
                                        warehouse: "East Coast",
                                        percent: 32,
                                      },
                                      {
                                        warehouse: "Midwest",
                                        percent: 22,
                                      },
                                    ].map((entry, idx) => (
                                      <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={3}
                                        // gap={1}
                                        key={entry.warehouse + idx}
                                      >
                                        <Paper
                                          sx={{
                                            p: 0.75,
                                            borderRadius: 0.75,
                                            border: `1px solid ${theme.palette.divider}`,
                                            bgcolor:
                                              theme.palette.mode === "dark"
                                                ? theme.palette.background
                                                    .default
                                                : theme.palette.grey[50],
                                            transition: "all 0.2s ease",
                                            mr: 1,
                                            "&:hover": {
                                              bgcolor:
                                                theme.palette.action.hover,
                                              transform: "translateY(-1px)",
                                            },
                                          }}
                                        >
                                          <Box sx={{ textAlign: "center" }}>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontFamily: "Poppins",
                                                fontWeight: 600,
                                                fontSize: "14px",
                                                color:
                                                  theme.palette.text.primary,
                                                mb: 0.125,
                                                lineHeight: 1.1,
                                              }}
                                            >
                                              {entry.warehouse}
                                            </Typography>

                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontFamily: "Poppins",
                                                fontWeight: 700,
                                                fontSize: "16px",
                                                color:
                                                  theme.palette.primary.main,
                                                lineHeight: 1.1,
                                              }}
                                            >
                                              {formatMetric(
                                                (item.forecast_adjusted.reduce(
                                                  (a, b) =>
                                                    a +
                                                    Number(
                                                      b.predicted_qty || 0,
                                                    ),
                                                  0,
                                                ) *
                                                  entry.percent) /
                                                  100,
                                              )}
                                            </Typography>
                                          </Box>
                                        </Paper>
                                      </Grid>
                                    ))}
                                  </Box>
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        ) : (
                          <></>
                        )}
                      </CardContent>
                    </Card>
                  </TimelineContent>
                </StyledTimelineItem>
              );
            })}
          </Timeline>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : theme.palette.grey[50],
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontFamily: "Poppins",
            fontWeight: 600,
            px: 3,
            py: 1.5,
            borderRadius: 2,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForecastHistoryModal;
